import { useRef, useState } from "react";
import { Alert, Button, Paper, Stack, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { toast } from "react-toastify";
import ConfirmDialog from "components/Modals/ConfirmDialog";
import SendInvoiceEmailDialog from "components/Invoice/SendInvoiceEmailDialog";
import { invalidateModuleFinance } from "components/ModulePayments/moduleFinanceQueries";
import { P } from "const/permissions";
import { useInvoicePrintTheme } from "hooks/useInvoicePrintTheme";
import InvoiceTemplate from "views/admin/Invoice/InvoiceTemplate";
import { getApiError } from "utils/apiError";
import { getInvoiceNumber } from "utils/invoiceDashboardUtils";
import { getInvoicePrintPageStyle } from "utils/invoicePrintTheme";
import { hasPermission } from "utils/permissionUtils";
import ModuleInvoiceDialog from "./ModuleInvoiceDialog";
import ModuleInvoiceSummary from "./ModuleInvoiceSummary";
import {
  deleteModuleInvoice,
  syncModuleInvoiceCharges,
  useModuleInvoice,
} from "./moduleInvoiceApi";
import { hasVerifiedPayment, PAID_LOCKED_NOTICE } from "./moduleInvoiceRules";

const CONFIRM = {
  sync: {
    title: "Sync from charges",
    message:
      "Rewrite the lines generated from this entry's charges? Lines you added by hand and descriptions you changed stay as they are.",
    label: "Sync",
    color: "primary",
    done: "Invoice lines updated from charges",
  },
  delete: {
    title: "Delete invoice",
    message:
      "Delete this invoice? Payments stay on the entry, and you can create a new invoice later.",
    label: "Delete",
    color: "error",
    done: "Invoice deleted",
  },
};

const FORBIDDEN = 403;

const ModuleInvoicePanel = ({ moduleType, moduleId, clientId }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: view, isLoading, isError, error, refetch } = useModuleInvoice(moduleType, moduleId);
  const [dialogMode, setDialogMode] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const printRef = useRef(null);
  const { darkPrint } = useInvoicePrintTheme();
  const print = useReactToPrint({
    contentRef: printRef,
    pageStyle: getInvoicePrintPageStyle(darkPrint),
  });

  const confirmMutation = useMutation({
    mutationFn: (action) =>
      action === "sync"
        ? syncModuleInvoiceCharges(moduleType, moduleId)
        : deleteModuleInvoice(moduleType, moduleId),
    onSuccess: (_, action) => {
      toast.success(CONFIRM[action].done);
      setConfirm(null);
      invalidateModuleFinance(queryClient, moduleType, moduleId, clientId);
    },
    onError: (err) => toast.error(getApiError(err, "Could not update the invoice")),
  });

  if (isError) {
    // No right to see invoices is not a fault worth showing on the Payments tab.
    if (error?.response?.status === FORBIDDEN) return null;
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => refetch()}>
            Retry
          </Button>
        }
      >
        {getApiError(error, "Could not load this entry's invoice.")}
      </Alert>
    );
  }
  if (isLoading || !view) return null;
  const invoice = view.invoice;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      {invoice ? (
        <Stack spacing={1.5}>
          <ModuleInvoiceSummary invoice={invoice} />
          {view.chargesChanged && (
            <Alert
              severity="warning"
              action={
                view.canEdit ? (
                  <Button color="inherit" size="small" onClick={() => setConfirm("sync")}>
                    Sync from charges
                  </Button>
                ) : undefined
              }
            >
              Charges changed since this invoice was generated.
            </Alert>
          )}
          {!view.canEdit && hasVerifiedPayment(invoice) && (
            <Typography variant="body2" color="text.secondary">
              {PAID_LOCKED_NOTICE}
            </Typography>
          )}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button size="small" variant="outlined" onClick={() => print()}>
              Preview / Print
            </Button>
            {hasPermission(P.INVOICE.SEND) && (
              <Button size="small" variant="outlined" onClick={() => setEmailOpen(true)}>
                Email
              </Button>
            )}
            {view.canEdit && (
              <Button size="small" variant="outlined" onClick={() => setDialogMode("edit")}>
                Edit
              </Button>
            )}
            <Button
              size="small"
              onClick={() => navigate(`/admin/invoice/view/${invoice.invoiceId}`)}
            >
              Open
            </Button>
            {view.canEdit && (
              <Button size="small" color="error" onClick={() => setConfirm("delete")}>
                Delete
              </Button>
            )}
          </Stack>
        </Stack>
      ) : (
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Typography variant="body2" color="text.secondary">
            No invoice for this entry yet.
          </Typography>
          {view.canCreate && (
            <Button variant="contained" size="small" onClick={() => setDialogMode("create")}>
              Create invoice
            </Button>
          )}
        </Stack>
      )}

      <ModuleInvoiceDialog
        open={Boolean(dialogMode)}
        mode={dialogMode}
        moduleType={moduleType}
        moduleId={moduleId}
        clientId={clientId}
        onClose={() => setDialogMode(null)}
      />
      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm ? CONFIRM[confirm].title : ""}
        message={confirm ? CONFIRM[confirm].message : ""}
        confirmLabel={confirm ? CONFIRM[confirm].label : "Confirm"}
        confirmColor={confirm ? CONFIRM[confirm].color : "primary"}
        loading={confirmMutation.isLoading}
        onConfirm={() => confirmMutation.mutate(confirm)}
        onCancel={() => setConfirm(null)}
      />
      {invoice && (
        <>
          <SendInvoiceEmailDialog
            open={emailOpen}
            onClose={() => setEmailOpen(false)}
            invoiceId={invoice.invoiceId}
            invoiceNumber={getInvoiceNumber(invoice)}
          />
          <InvoiceTemplate printData={invoice} contentRef={printRef} />
        </>
      )}
    </Paper>
  );
};

export default ModuleInvoicePanel;
